import { useCallback, useEffect, useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Title,
  useDataProvider,
  useNotify,
  usePermissions,
  useRedirect,
  useShowController,
} from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { formatListDate, formatWeekdayFull } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";
import type { HouseVisitSlotRecord, HouseVisitStopRecord } from "../types";
import SortableStopRow from "./SortableStopRow";
import { UploadHofListDialog } from "./UploadHofListDialog";

type ItsChoice = {
  id: string | number;
  ITS_ID: string;
  Full_Name?: string;
};

type ApiRows<T> = { rows?: T[]; count?: number };

const SlotPlanner = () => {
  const { record, isPending, error } = useShowController<HouseVisitSlotRecord>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const { permissions } = usePermissions();

  const [stops, setStops] = useState<HouseVisitStopRecord[]>([]);
  const [loadingStops, setLoadingStops] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hofQuery, setHofQuery] = useState("");
  const [hofOptions, setHofOptions] = useState<ItsChoice[]>([]);
  const [hofLoading, setHofLoading] = useState(false);
  const [selectedHof, setSelectedHof] = useState<ItsChoice | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const canEdit = hasPermission(permissions, "houseVisitStops.edit");
  const canCreate = hasPermission(permissions, "houseVisitStops.create");
  const canDelete = hasPermission(permissions, "houseVisitStops.delete");

  const slotId = record?.id != null ? String(record.id) : "";

  const loadStops = useCallback(async () => {
    if (!slotId) return;
    setLoadingStops(true);
    try {
      const { data } = await dataProvider.getList("houseVisitStops", {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "sortOrder", order: "ASC" },
        filter: { slotId },
      });
      setStops((data as HouseVisitStopRecord[]).slice().sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err: unknown) {
      notify((err as Error)?.message || "Failed to load stops", { type: "warning" });
      setStops([]);
    } finally {
      setLoadingStops(false);
    }
  }, [dataProvider, notify, slotId]);

  useEffect(() => {
    void loadStops();
  }, [loadStops]);

  useEffect(() => {
    let cancelled = false;
    const q = hofQuery.trim();
    if (q.length < 2) {
      setHofOptions([]);
      return;
    }
    setHofLoading(true);
    const timer = window.setTimeout(() => {
      const filter = /^\d+$/.test(q) ? { ITS_ID: q, isHOF: true } : { q, isHOF: true };
      dataProvider
        .getList("itsdata", {
          pagination: { page: 1, perPage: 20 },
          sort: { field: "Full_Name", order: "ASC" },
          filter,
        })
        .then(({ data }) => {
          if (!cancelled) setHofOptions(data as ItsChoice[]);
        })
        .catch(() => {
          if (!cancelled) setHofOptions([]);
        })
        .finally(() => {
          if (!cancelled) setHofLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [dataProvider, hofQuery]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const stopIds = useMemo(() => stops.map((s) => String(s.id)), [stops]);

  const persistReorder = async (ordered: HouseVisitStopRecord[]) => {
    if (!slotId) return;
    setBusy(true);
    try {
      const res = await callApi({
        location: "houseVisitStops",
        method: "PUT",
        id: `${slotId}/reorder`,
        data: { orderedStopIds: ordered.map((s) => String(s.id)) },
      });
      const body = res.data as ApiRows<HouseVisitStopRecord>;
      if (body.rows) {
        setStops(body.rows.slice().sort((a, b) => a.sortOrder - b.sortOrder));
      } else {
        setStops(ordered.map((s, i) => ({ ...s, sortOrder: i })));
      }
    } catch (err: unknown) {
      notify((err as Error)?.message || "Failed to reorder", { type: "warning" });
      void loadStops();
    } finally {
      setBusy(false);
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    if (!canEdit || busy) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = stopIds.indexOf(String(active.id));
    const newIndex = stopIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(stops, oldIndex, newIndex).map((s, i) => ({
      ...s,
      sortOrder: i,
    }));
    setStops(next);
    void persistReorder(next);
  };

  const addHof = async () => {
    if (!selectedHof || !slotId || !canCreate) return;
    const its = selectedHof.ITS_ID?.trim();
    if (!its) return;
    setBusy(true);
    try {
      await callApi({
        location: "houseVisitStops",
        method: "POST",
        data: { slotId, hofItsNos: [its] },
      });
      setSelectedHof(null);
      setHofQuery("");
      notify("Stop added", { type: "info" });
      await loadStops();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to add stop";
      notify(message, { type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  const removeStop = async (id: string) => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await dataProvider.delete("houseVisitStops", { id, previousData: { id } });
      notify("Stop removed", { type: "info" });
      await loadStops();
    } catch (err: unknown) {
      notify((err as Error)?.message || "Failed to remove stop", { type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  if (isPending) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !record) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Could not load this slot.</Typography>
        <Button sx={{ mt: 1 }} onClick={() => redirect("/houseVisitSlots")}>
          Back to slots
        </Button>
      </Box>
    );
  }

  const capacity = Number(record.capacity ?? 0) || 10;
  const filled = stops.length;
  const capacityPct = Math.min(100, Math.round((filled / capacity) * 100));
  const isFull = filled >= capacity;
  const titleDate = formatListDate(record.date, { utc: true });
  const weekday = formatWeekdayFull(record.date, { utc: true });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, pb: 6, maxWidth: 720, mx: "auto" }}>
      <Title title={`${weekday} · ${titleDate}`} />

      <Button
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => redirect("/houseVisitSlots")}
        sx={{ mb: 2, px: 0, minWidth: 0, color: "text.secondary" }}
      >
        Slots
      </Button>

      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: { xs: "1.35rem", sm: "1.6rem" }, letterSpacing: "-0.02em" }}
          >
            {weekday}
          </Typography>
          {record.label?.trim() ? (
            <Chip size="small" label={record.label.trim()} variant="outlined" />
          ) : null}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {titleDate}
        </Typography>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.08 : 0.04),
          borderColor: (t) => alpha(t.palette.primary.main, 0.22),
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 0.75 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Capacity
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {filled}/{capacity} stops
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={capacityPct}
          color={isFull ? "warning" : "primary"}
          sx={{
            height: 8,
            borderRadius: 999,
            bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
            "& .MuiLinearProgress-bar": { borderRadius: 999 },
          }}
        />
        {isFull ? (
          <Typography variant="caption" color="warning.main" sx={{ mt: 0.75, display: "block" }}>
            Slot is full — remove a stop before adding another.
          </Typography>
        ) : null}
      </Paper>

      {canCreate ? (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Add stop
            </Typography>
            <Button
              size="small"
              startIcon={<UploadFileIcon />}
              disabled={busy || isFull}
              onClick={() => setUploadOpen(true)}
            >
              Upload list
            </Button>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ sm: "stretch" }}
          >
            <Autocomplete
              fullWidth
              options={hofOptions}
              loading={hofLoading}
              value={selectedHof}
              onChange={(_e, value) => setSelectedHof(value)}
              onInputChange={(_e, value) => setHofQuery(value)}
              getOptionLabel={(o) =>
                o.Full_Name ? `${o.Full_Name} (${o.ITS_ID})` : o.ITS_ID || String(o.id)
              }
              isOptionEqualToValue={(a, b) => a.ITS_ID === b.ITS_ID}
              filterOptions={(x) => x}
              disabled={busy || isFull}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search HOF"
                  placeholder="Name or ITS number"
                  size="medium"
                  helperText={
                    hofQuery.trim().length > 0 && hofQuery.trim().length < 2
                      ? "Type at least 2 characters"
                      : " "
                  }
                />
              )}
            />
            <Button
              variant="contained"
              onClick={() => {
                void addHof();
              }}
              disabled={!selectedHof || busy || isFull}
              sx={{
                minWidth: { sm: 120 },
                minHeight: 56,
                fontWeight: 700,
                alignSelf: { sm: "flex-start" },
                mt: { sm: 0 },
              }}
            >
              Add
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {canCreate ? (
        <UploadHofListDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          slotId={slotId}
          onAdded={() => loadStops()}
        />
      ) : null}

      {loadingStops ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : stops.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            py: 5,
            px: 2.5,
            textAlign: "center",
            bgcolor: "action.hover",
            borderStyle: "dashed",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 1.5,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <PersonSearchIcon />
          </Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            No stops yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, mx: "auto" }}>
            {canCreate
              ? "Search for a head of family above to build this day’s route."
              : "Stops will appear here once they are assigned."}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {canEdit ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Drag handles to reorder the route
            </Typography>
          ) : null}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={stopIds} strategy={verticalListSortingStrategy}>
              <Stack spacing={1}>
                {stops.map((stop) => (
                  <SortableStopRow
                    key={String(stop.id)}
                    stop={stop}
                    busy={busy || !canEdit}
                    canRemove={canDelete}
                    onRemove={(id) => {
                      void removeStop(id);
                    }}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Box>
      )}
    </Box>
  );
};

export default SlotPlanner;
